import { Injectable } from '@angular/core';
import { convertCloserAddressToBaseTransactionAddress } from '@core/util/address';
import { BusinessAddress, BusinessUser } from '@models/businesses';
import {
	AssociatedContactType,
	ClientType,
	PersonClient,
} from '@models/clients';
import { Identification, IdentificationPriority } from '@models/identification';
import {
	Encumbrance,
	EncumbranceActionType,
	EncumbranceType,
} from '@models/encumbrance';
import {
	ComplianceRequestStatuses,
	Department,
	Jurisdiction,
} from '@models/jurisdictions';
import {
	BusinessMatterClientWithAssociations,
	CondoUnit,
	MatterAdjustmentPropertyTaxCalculated,
	MatterFileType,
	MatterFireInsurance,
	MatterPropertyDetails,
	MatterTitle,
	PersonMatterClientWithAssociations,
	PropertyType,
	RegistrationMethodType,
	SpousalStatus,
	TaxBill,
} from '@models/matter';
import {
	FundingType,
	MatterMortgage,
	MatterMortgageWithAssociations,
	ValidationResult,
} from '@models/mortgage';
import {
	FinalReport,
	MortgageRequestBase,
	RequestForFundMortgage,
	RequestForFundPropertyDetails,
	RequestForFunds,
	RequestForFundsEncumbranceAction,
	RequestForFundsEncumbranceType,
	RequestForFundsEncumbrances,
	RequestForFundsFireInsurance,
	RequestForFundsFundingDetails,
	RequestForFundsGuarantor,
	RequestForFundsIdentification,
	RequestForFundsLawyer,
	RequestForFundsMortgagor,
	RequestForFundsPerson,
	RequestForFundsProperty,
	RequestForFundsPropertyMortgageDetails,
	RequestForFundsPropertyRegistrationMethod,
	RequestForFundsPropertyTaxDetails,
	RequestForFundsPropertyType,
	RequestForFundsTitleInsurance,
} from '@models/mortgages/closer-sync-request-for-funds';
import { BaseTransactionAddress } from '@models/mortgages/mortgage-base';
import {
	MatterRequisitionPrecedent,
	RequisitionPrecedentFieldNames,
} from '@models/requisitions';
import { MatterManualTitleInsurance } from '@models/title-insurance';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Selector } from '@ngxs/store';
import {
	GetMortgageLenderPolicy,
	getLegalDescription,
} from '@services/mortgages/llc.service';
import { BusinessState } from '@state/business';
import { MatterClients } from '@state/global/matter_clients';
import { MatterMortgageGuarantors } from '@state/global/matter_guarantors';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { MatterEncumbrancesState } from '@state/matter/encumbrances';
import { MatterJurisdictionSelectors } from '@state/matter/jurisdictions/jurisdictions.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { ValidationTemplate } from '@state/matter/matter_mortgages/models';
import { MatterRequisitionsState } from '@state/matter/requisitions/matter-requisitions.state';
import { MatterTitlesState } from '@state/matter/titles/titles.state';

import { has, isSet } from 'lodash';

const RegistrationMethodLookup: {
	[id in RegistrationMethodType]: RequestForFundsPropertyRegistrationMethod;
} = {
	[RegistrationMethodType.LAND_TITLES]: 'Land Titles',
	[RegistrationMethodType.REGISTRY]: 'Registry',
};

const EncumbranceActionLookup: {
	[id in EncumbranceActionType]: RequestForFundsEncumbranceAction;
} = {
	[EncumbranceActionType.DISCHARGE]: 'DISCHARGE',
	[EncumbranceActionType.REMAIN]: 'REMAIN',
	[EncumbranceActionType.BRIDGE]: 'BRIDGE',
	[EncumbranceActionType.ASSUME]: 'ASSUME',
};

const EncumbranceTypeLookup: {
	[id in EncumbranceType]: RequestForFundsEncumbranceType;
} = {
	[EncumbranceType.MORTGAGE]: 'MORTGAGE',
	[EncumbranceType.LIEN]: 'LIEN',
	[EncumbranceType.NOTICE_OF_SECURITY_INTEREST]: 'NOTICE_OF_SECURITY_INTEREST',
};

@Injectable({
	providedIn: 'root',
})
export class RequestForFundsSelectors {
	@SelectSnapshot(SelectedMatter.getSelected) matter: SelectedMatter;
	@SelectSnapshot(SelectedMatter.assignedLawyerUser)
	assignedLawyer: BusinessUser;
	@SelectSnapshot(SelectedMatter.office) office: BusinessAddress;

	@Selector([
		SelectedMatter.office,
		SelectedMatter.assignedLawyerUser,
		BusinessState.businessName,
	])
	static GetLawyerDetails(
		office: BusinessAddress,
		assignedLawyer: BusinessUser,
		businessName: string,
	): RequestForFundsLawyer {
		return {
			userId: assignedLawyer?.closerSyncId,
			firmName: office?.name ?? businessName,
			email: assignedLawyer?.email,
			telephone: assignedLawyer?.phoneNumber,
			faxNumber: assignedLawyer?.faxNumber,
			address: convertCloserAddressToBaseTransactionAddress(office),
			firstName: assignedLawyer?.firstName,
			middleName: null,
			lastName: assignedLawyer?.lastName,
			signature: assignedLawyer?.signature,
		};
	}

	@Selector([SelectedMatter.fireInsurance])
	static GetFireInsurance(
		fireInsurance: MatterFireInsurance,
	): RequestForFundsFireInsurance {
		if (!fireInsurance) {
			return null;
		}

		return {
			amount: fireInsurance?.coverageAmount,
			fireInsuranceCompany: fireInsurance?.fireInsuranceCompany?.name,
			policyNumber: fireInsurance?.policyNumber, // policy number
			activeDate: fireInsurance?.activeDate, // YYYY-MM-DD
			expiryDate: fireInsurance?.expiryDate, // YYYY-MM-DD
			address: convertCloserAddressToBaseTransactionAddress(
				fireInsurance?.fireInsuranceCompany?.address,
			),
			beneficiaryBranch: {
				//TODO: Needs to be added to the UI
				payee: '',
				address: {
					streetAddress1: '',
					city: '',
					province: '',
					postalCode: '',
					country: '',
				},
			},
			guaranteedReplacementCoverage:
				fireInsurance?.guaranteedReplacementCoverage,
		};
	}

	@Selector([SelectedMatter.propertyDetails, MatterTitlesState.getAll])
	static GetLegalDescription(
		propertyDetails: MatterPropertyDetails,
		matterTitles: MatterTitle[],
	): string {
		return getLegalDescription(
			propertyDetails,
			matterTitles,
			propertyDetails?.propertyType?.type === PropertyType.CONDO,
		);
	}

	@Selector([
		SelectedMatter.propertyDetails,
		MatterTitlesState.getDwellingUnit,
		MatterTitlesState.getPins,
		RequestForFundsSelectors.GetLegalDescription,
		MatterJurisdictionSelectors.getPrimary,
	])
	static GetPropertyDetails(
		propertyDetails: MatterPropertyDetails,
		dwellingUnit: CondoUnit,
		pins: string[],
		legalDescription: string,
		jurisdiction: Jurisdiction,
	): RequestForFundPropertyDetails {
		let propertyType: RequestForFundsPropertyType = 'Single';
		let numberOfUnits = 1;
		let condoPlanNumber = null;

		if (propertyDetails?.propertyType?.type === PropertyType.CONDO) {
			propertyType = 'Condo';
			condoPlanNumber = dwellingUnit?.pinNumber;
		}

		if (propertyDetails?.propertyType?.multiUnit) {
			numberOfUnits = propertyDetails?.propertyType?.numberOfUnits ?? 1;
			propertyType = 'Multi-Family';
		}

		const rollNumbers = [];
		if (propertyDetails?.rollNumber) {
			rollNumbers.push(propertyDetails?.rollNumber);
		}

		// TODO: Review fields against LLC
		return {
			address: convertCloserAddressToBaseTransactionAddress(
				propertyDetails?.address,
			),
			pins: pins ?? null,
			legalDescription,
			propertyType: propertyType,
			municipalTaxOffice: jurisdiction?.name,
			rollNumbers: rollNumbers,
			registrationMethod: propertyDetails?.registrationMethod
				? RegistrationMethodLookup[propertyDetails?.registrationMethod]
				: null, // "Registry" or "Land Titles"
			landRegistryOffice: propertyDetails?.lro?.selected
				? `${propertyDetails?.lro?.selected?.name} No. ${propertyDetails?.lro?.selected?.id}`
				: null,
			occupancyType: propertyDetails?.propertyType?.tenanted
				? 'Rental'
				: 'Owner Occupied',
			numberOfUnits: numberOfUnits,
			condoUnitPlanNumber: condoPlanNumber, // llc collects a lot more - guessing this is dwelling unit
			survey: {
				surveyDate: propertyDetails?.surveyDate, // YYYY-MM-DD
				surveyNumber: null, //propertyDetails?.surveyNumber, //TODO: Add to the UI
				surveyor: propertyDetails?.surveyorName,
			},
			interestType: propertyDetails?.interestEstate,
		};
	}

	@Selector([MatterEncumbrancesState.getSorted, SelectedMatter.matterFileType])
	static GetExistingMortgages(
		encumbrances: Encumbrance[],
		fileType: MatterFileType,
	): RequestForFundsEncumbrances[] {
		let undertakingSelection;
		if (fileType === MatterFileType.REFINANCE) {
			undertakingSelection = 'I Undertake';
		} else if (encumbrances.length > 0) {
			undertakingSelection = 'Not Applicable';
		} else {
			undertakingSelection = 'Received Undertaking';
		}

		if (fileType === MatterFileType.REFINANCE) {
			return encumbrances.map((encumbrance) => {
				return {
					lenderName:
						encumbrance?.financialInstitution?.name ??
						encumbrance?.privateLender?.fullName,
					remainPriority:
						encumbrance.action === EncumbranceActionType.DISCHARGE
							? null
							: encumbrance?.priorityAfterClosing,
					postponed:
						encumbrance.action === EncumbranceActionType.DISCHARGE
							? null
							: encumbrance.postponeMortgage,
					undertakingSelection: undertakingSelection, // TODO: need logic on this
					remainingAmount: encumbrance.outstandingAmount, // cents
					registrationNumber: encumbrance.registrationNumber,
					registrationMethod: 'Registry', // TODO: Need logic and pointed to fields
					registrationDate: encumbrance.registrationDate, // YYYY-MM-DD
					action: EncumbranceActionLookup[encumbrance.action] ?? null, // TODO: Need more mappings & logic
					encumbranceType: EncumbranceTypeLookup[encumbrance.type],
				};
			});
		}
		if (fileType === MatterFileType.PURCHASE) {
			return [];
		}

		return [];
	}

	@Selector([
		MatterJurisdictionSelectors.getPrimaryTaxDepartment,
		MatterRequisitionsState.getAll,
		AdjustmentsSelector.allPropertyTaxAdjustmentCalculations,
	])
	static GetPropertyTaxDetails(
		taxDepartment: Department,
		requisitions: MatterRequisitionPrecedent[],
		taxAdjustments: MatterAdjustmentPropertyTaxCalculated[],
	): RequestForFundsPropertyTaxDetails {
		let annualTaxAmount = 0;

		taxAdjustments.forEach((taxAdjustment) => {
			if (taxAdjustment) {
				switch (taxAdjustment.adjustment?.taxBill) {
					case TaxBill.ESTIMATED:
					case TaxBill.FINAL:
						annualTaxAmount += taxAdjustment.adjustment.total;
						break;
					case TaxBill.INTERIM:
					case TaxBill.PREVIOUS:
						annualTaxAmount += taxAdjustment.estimatedIncrease;
						break;
				}
			}
		});

		const complianceRequests = taxDepartment?.complianceRequests;
		let tax;
		if (complianceRequests) {
			tax = Object.values(complianceRequests)[0];
		}

		if (
			!tax ||
			tax?.complianceRequestStatus === ComplianceRequestStatuses.CLEARED ||
			!tax?.requisitionId
		) {
			return {
				annualTaxAmount,
				taxesOutstanding: false,
				taxAmountInArrears: 0,
			};
		}

		const taxRequisition = requisitions.find(
			(requisition) => requisition.id === tax?.requisitionId,
		);

		return {
			annualTaxAmount,
			taxesOutstanding: true,
			taxAmountInArrears:
				(taxRequisition?.fields?.[
					RequisitionPrecedentFieldNames.INSTRUMENT_AMOUNT
				]?.value as number) ?? null,
		};
	}

	@Selector([
		SelectedMatter.nextInstallmentDate,
		MatterMortgagesState.getSelectedMortgage,
	])
	static GetPropertyMortgageDetails(
		installmentDate: string,
		selectedMortgage: MatterMortgageWithAssociations,
	): RequestForFundsPropertyMortgageDetails {
		return {
			taxesPaidTo: selectedMortgage?.commitmentDate, // YYYY-MM-DD
			registrationNumber: selectedMortgage?.registrationNumber,
			registrationDate: selectedMortgage?.registrationDate, // YYYY-MM-DD
			nextInstallmentDate: installmentDate, // YYYY-MM-DD
		};
	}

	@Selector([
		MatterMortgagesState.getSelectedMortgage,
		SelectedMatter?.closingDate,
	])
	static GetMortgageDetails(
		mortgage: MatterMortgage,
		closingDate: string,
	): RequestForFundMortgage {
		return {
			mortgageLoanNumber: mortgage?.referenceNumber,
			principalAmount: mortgage?.amount,
			interestRateAsReg: mortgage?.interestRateAsReg,
			maturityDateAsReg: mortgage?.maturityDateAsReg, // YYYY-MM-DD
			totalRegularAmountOfEachPaymentAsReg: mortgage?.regularPaymentsAsReg, // cents
			paymentFrequencyAsReg: mortgage?.paymentFrequencyAsReg,
			interestAdjustmentDateAsReg: mortgage?.interestAdjustmentDateAsReg, // YYYY-MM-DD
			netAdvanced: mortgage?.netAdvanced ?? 0, // cents
			amountType: mortgage?.amountType, // TODO: needs to be pulled in from the Cloer SYNC mortgage "Full Mortgage Proceeds" | "Construction Mortgage" | "Builders Interest Rate Buy Down"
			closingDate: closingDate, // YYYY-MM-DD
			mortgagePriority: mortgage?.priority, // 1 | 2 | 3 | 4 | 5
			defaultInsuranceProvider: mortgage?.defaultInsuranceProvider, // TODO: needs to be pulled in from the Clsoer SYNC mortgage
			defaultInsuranceReferenceNumber:
				mortgage?.defaultInsuranceReferenceNumber, // TODO: needs to be pulled in from the Cloer SYNC mortgage
			chargeType: mortgage?.chargeType,
			useTrustAccountOnInstructions:
				mortgage?.fundingDetails?.useTrustAccountOnInstructions,
		};
	}

	@Selector([
		RequestForFundsSelectors.GetFireInsurance,
		RequestForFundsSelectors.GetPropertyDetails,
		RequestForFundsSelectors.GetExistingMortgages,
		RequestForFundsSelectors.GetPropertyMortgageDetails,
		RequestForFundsSelectors.GetPropertyTaxDetails,
	])
	static GetProperty(
		fireInsurance: RequestForFundsFireInsurance,
		propertyDetails: RequestForFundPropertyDetails,
		encumbrances: RequestForFundsEncumbrances[],
		propertyMortgageDetails: RequestForFundsPropertyMortgageDetails,
		taxDetails: RequestForFundsPropertyTaxDetails,
	): RequestForFundsProperty {
		return {
			...propertyDetails,
			...propertyMortgageDetails,
			...taxDetails,
			encumbrances,
			fireInsurance,
		};
	}

	@Selector([
		SelectedMatter.getTitleInsurance,
		MatterMortgagesState.getSelectedMortgage,
	])
	static getTitleInsurance(
		titleInsurance: MatterManualTitleInsurance,
		mortgage: MatterMortgage,
	): RequestForFundsTitleInsurance {
		if (!titleInsurance) {
			return {
				solicitorCertification: 'Title Opinion',
			};
		}

		const lenderPolicy = GetMortgageLenderPolicy(
			titleInsurance?.policies,
			mortgage.priority,
		);

		return {
			insured: titleInsurance?.policies?.length > 0, // this is set to true if the title insurance is not insured and they need to provide a requirementConfirmation
			solicitorCertification: 'Title Insurance', //  Protocol | Title Opinion (Can only be title opinion for the Ontario)
			companyName: titleInsurance?.companyName,
			policyNumber: lenderPolicy?.number,
			amount: lenderPolicy?.insuredAmount, // cents
			policyDate: titleInsurance?.effectiveDate, // YYYY-MM-DD
		};
	}

	@Selector([MatterClients.get_borrowers, MatterClients.get_buyers])
	static GetMortgagors(
		borrowers: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		buyers: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
	) {
		const result: RequestForFundsMortgagor[] = [];
		const clients = [...borrowers, ...buyers];
		clients.forEach((client) => {
			let baseClient: Partial<RequestForFundsMortgagor> =
				RequestForFundsSelectors.BuildBaseClient(client);
			if (client?.type === ClientType.PERSON) {
				const spousalInfo = RequestForFundsSelectors.buildSpouse(
					client as PersonMatterClientWithAssociations,
				);
				baseClient = { ...baseClient, ...spousalInfo };
			}
			const mortgagor: RequestForFundsMortgagor = {
				mortgagorId: '7cb776c5-8c12-4b1a-84aa-9941b815d873',
				mortgagorType:
					client?.type === ClientType.PERSON ? 'PERSON' : 'BUSINESS',
				...baseClient,
			};

			result.push(mortgagor);
		});

		return result;
	}

	@Selector([MatterMortgageGuarantors.getGuarantorsForSelectedMortgage])
	static GetGuarantors(
		guarantors: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
	) {
		const result: RequestForFundsGuarantor[] = [];
		guarantors.forEach((client) => {
			let baseClient: Partial<RequestForFundsGuarantor> =
				RequestForFundsSelectors.BuildBaseClient(client);
			if (client?.type === ClientType.PERSON) {
				const spousalInfo = RequestForFundsSelectors.buildSpouse(
					client as PersonMatterClientWithAssociations,
				);
				baseClient = { ...baseClient, ...spousalInfo };
			}
			const guarantor: RequestForFundsGuarantor = {
				guarantorId: '7cb776c5-8c12-4b1a-84aa-9941b815d873',
				guarantorType:
					client?.type === ClientType.PERSON ? 'PERSON' : 'BUSINESS',
				...baseClient,
			};

			result.push(guarantor);
		});

		return result;
	}

	static BuildBaseClient(
		mortgagor:
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations,
	): RequestForFundsPerson {
		const base: RequestForFundsPerson = {};

		if (mortgagor?.type === ClientType.PERSON) {
			base.firstName = mortgagor?.firstName;
			base.middleName = mortgagor?.middleNames;
			base.lastName = mortgagor?.lastName;
			base.occupation = mortgagor?.occupation;
			base.identifications =
				RequestForFundsSelectors.buildIdentifications(mortgagor);
			base.signatories = [];
		} else {
			base.companyName = mortgagor?.name;
			base.identifications = [];

			const signingOfficers =
				mortgagor?.clientAssociations?.filter((x) =>
					x?.associations?.types
						? Object.keys(x.associations.types).includes(
								AssociatedContactType.SIGNING_OFFICER,
							)
						: false,
				) ?? [];

			if (signingOfficers.length > 0) {
				base.firstName = (
					signingOfficers[0]?.client as PersonClient
				)?.firstName;
				base.middleName = (
					signingOfficers[0]?.client as PersonClient
				)?.middleNames;
				base.lastName = (signingOfficers[0]?.client as PersonClient)?.lastName;
			}

			base.phone = mortgagor?.phone;
			base.occupation = (mortgagor as unknown as PersonClient)?.occupation;

			base.identifications = [];
			base.signatories =
				signingOfficers.map((signingOfficer) => ({
					firstName: (signingOfficer?.client as PersonClient)?.firstName,
					middleName: (signingOfficer?.client as PersonClient)?.middleNames,
					lastName: (signingOfficer?.client as PersonClient)?.lastName,
					occupation:
						signingOfficer?.associations?.types?.[
							AssociatedContactType.SIGNING_OFFICER
						]?.position,
					birthDate: (signingOfficer?.client as PersonClient)?.dateOfBirth,
					identifications: RequestForFundsSelectors.buildIdentifications(
						signingOfficer?.client as PersonClient,
					),
				})) ?? [];
		}

		base.phone = mortgagor?.phone;
		base.address = convertCloserAddressToBaseTransactionAddress(
			mortgagor?.reportAddress ?? mortgagor?.serviceAddress,
		);

		return base;
	}

	static buildIdentifications(
		client: PersonClient,
	): RequestForFundsIdentification[] {
		const result: RequestForFundsIdentification[] = [];

		if (client?.identification?.[IdentificationPriority.PRIMARY]) {
			result.push(
				RequestForFundsSelectors.buildIdentification(
					client?.identification?.[IdentificationPriority.PRIMARY],
				),
			);
		}

		if (client?.identification?.[IdentificationPriority.SECONDARY]) {
			result.push(
				RequestForFundsSelectors.buildIdentification(
					client?.identification?.[IdentificationPriority.SECONDARY],
				),
			);
		}

		return result;
	}

	static buildIdentification(
		identification: Identification,
	): RequestForFundsIdentification {
		return {
			type: identification?.type,
			number: identification?.number,
			expiryDate: identification?.expirationDate, // YYYY-MM-DD
			issuingJurisdiction: identification?.jurisdiction,
			issuingCountry: identification?.country,
			verifiedOn: identification?.verifiedBy, // YYYY-MM-DD
			name: identification?.fullName,
		};
	}

	static buildSpouse(
		mortgagor: PersonMatterClientWithAssociations,
	): Partial<RequestForFundsMortgagor> {
		const baseClient: Partial<RequestForFundsMortgagor> = {};

		if (
			mortgagor?.flaClient &&
			mortgagor?.fla?.spousalStatus === SpousalStatus.CONSENTING
		) {
			baseClient.spouseFirstName = mortgagor?.flaClient?.firstName;
			baseClient.spouseMiddleName = mortgagor?.flaClient?.middleNames;
			baseClient.spouseLastName = mortgagor?.flaClient?.lastName;
		}

		return baseClient;
	}

	@Selector([MatterMortgagesState.getSelectedMortgage])
	static GetFundingDetails(
		mortgage: MatterMortgage,
	): RequestForFundsFundingDetails {
		if (!mortgage?.fundingDetails) {
			return null;
		}

		const { fundingDetails } = mortgage;

		return {
			requiresFunding: fundingDetails?.type !== FundingType.NO_FUNDING_REQUIRED,
			bridgeFunding: {
				required: fundingDetails?.bridgeFunding?.required ?? false,
			},
			fundingMethod: fundingDetails?.type ?? null,
			trustAccount: {
				accountName: fundingDetails?.trustAccount?.payeeName,
				accountNumber: fundingDetails?.trustAccount?.accountNumber,
				transit: fundingDetails?.trustAccount?.transit,
				institutionName: fundingDetails?.trustAccount?.institutionName,
				institutionCode: fundingDetails?.trustAccount?.institutionNumber,
				address: convertCloserAddressToBaseTransactionAddress(
					fundingDetails?.trustAccount?.address,
				),
				sameAsInstructions: false,
				voidCheque: fundingDetails?.trustAccount?.voidCheque,
			},
			chequePickupBranch: {
				name: fundingDetails?.localBranch?.name,
				transit: fundingDetails?.localBranch?.transitNumber,
				institution: fundingDetails?.localBranch?.institutionNumber,
				address: convertCloserAddressToBaseTransactionAddress(
					fundingDetails?.localBranch?.address,
				),
			},
			courierDetails: {
				accountNumber: fundingDetails?.accountNumber,
			},
			useTrustAccountOnInstructions:
				fundingDetails?.useTrustAccountOnInstructions,
		};
	}

	@Selector([
		SelectedMatter.matterFileType,
		MatterMortgagesState.getSelectedMortgage,
		RequestForFundsSelectors.GetLawyerDetails,
		RequestForFundsSelectors.GetProperty,
		RequestForFundsSelectors.getTitleInsurance,
		RequestForFundsSelectors.GetMortgageDetails,
		RequestForFundsSelectors.GetMortgagors,
		RequestForFundsSelectors.GetGuarantors,
	])
	static RequestBase(
		matterFileType: MatterFileType,
		matterMortgage: MatterMortgage,
		lawyer: RequestForFundsLawyer,
		property: RequestForFundsProperty,
		titleInsurance: RequestForFundsTitleInsurance,
		mortgage: RequestForFundMortgage,
		mortgagors: RequestForFundsMortgagor[],
		guarantors: RequestForFundsGuarantor[],
	): MortgageRequestBase {
		const lender = matterMortgage?.lenders
			? Object?.values(matterMortgage.lenders)?.[0]
			: null;

		return {
			instructionType: matterFileType,
			instructionId: matterMortgage?.relatedMortgageId,
			lender: {
				id: lender?.closerSyncId,
				name: lender?.name,
				institutionNumber: lender?.institutionNumber,
			},
			mortgagee: {
				id: lender?.closerSyncId,
				name: lender?.name,
				institutionNumber: lender?.institutionNumber,
			},
			lawyer,
			properties: [property],
			titleInsurance,
			mortgage,
			guarantors,
			mortgagors,
		};
	}

	@Selector([RequestForFundsSelectors.RequestBase])
	static FinalReport(base: MortgageRequestBase): FinalReport {
		return base;
	}

	static validateFields(
		requestForFunds: RequestForFunds,
		fields,
		parentField = '',
	) {
		const missingFieldList = [];

		for (const field of fields) {
			if (!isSet(field.field)) {
				const fullFieldName = parentField
					? `${parentField}.${field.field}`
					: field.field;
				const label = field?.friendlyLabel ?? '';

				if (field.required) {
					let hasField;

					switch (parentField) {
						case 'properties':
							hasField = has(requestForFunds, `properties[0].${field.field}`);
							break;
						case 'properties.address':
							hasField = has(
								requestForFunds,
								`properties[0].address.${field.field}`,
							);
							break;
						default:
							hasField = has(requestForFunds, fullFieldName);
					}

					if (!hasField) {
						missingFieldList.push({
							parentField: parentField?.split('.')?.[0],
							label,
							fullFieldName,
						});
					}

					if (field.fields) {
						const nestedValidationObject =
							RequestForFundsSelectors.validateFields(
								requestForFunds,
								field.fields,
								fullFieldName,
							);
						missingFieldList.push(...nestedValidationObject);
					}
				}
			}
		}

		return missingFieldList;
	}

	static validateMortgageFromTemplate(
		requestForFunds: RequestForFunds,
		validationTemplate: ValidationTemplate,
	) {
		const result = RequestForFundsSelectors.validateFields(
			requestForFunds,
			validationTemplate.fields,
		);

		return result;
	}

	@Selector([
		RequestForFundsSelectors.RequestForFunds,
		MatterMortgagesState.requestForFundsValidationTemplate,
	])
	static ValidateRequestForFunds(
		base: RequestForFunds,
		validationTemplate: ValidationTemplate,
	): ValidationResult {
		if (!validationTemplate) {
			return;
		}

		const result = RequestForFundsSelectors.validateMortgageFromTemplate(
			base,
			validationTemplate,
		);

		return {
			missingFields: result,
			validationType: 'REQUEST_FOR_FUNDS',
			isValid: result.length === 0,
		};
	}

	@Selector([RequestForFundsSelectors.RequestForFunds])
	static ValidateFundingDetails(base: RequestForFunds): ValidationResult {
		const result = [];

		const fundingMethod = base?.fundingDetails?.fundingMethod;

		if (!fundingMethod) {
			result.push({
				parentField: 'fundingDetails',
				label: 'Funding Details',
				fullFieldName: 'fundingDetails.fundingMethod',
			});
		}

		if (fundingMethod) {
			let isValid = true;
			let resultObject = {};

			switch (fundingMethod) {
				case FundingType.BRANCH_PICKUP: {
					const branchDetails = base?.fundingDetails?.chequePickupBranch;
					isValid =
						branchDetails?.name &&
						branchDetails?.transit &&
						branchDetails?.institution &&
						RequestForFundsSelectors.IsAddressValid(branchDetails?.address);
					resultObject = {
						parentField: 'fundingDetails.chequePickupBranch',
						label: 'Pickup Branch Information',
						fullFieldName: 'fundingDetails.chequePickupBranch',
					};
					break;
				}
				case FundingType.WIRE_TRANSFER: {
					const accountDetails = base?.fundingDetails?.trustAccount;
					const { accountNumber, transit, institutionName, institutionCode } =
						accountDetails || {};
					isValid = !!(
						accountNumber &&
						transit &&
						(institutionName || institutionCode)
					);

					resultObject = {
						parentField: 'fundingDetails.trustAccount',
						label: 'Trust Account Information',
						fullFieldName: 'fundingDetails.trustAccount',
					};
					break;
				}
				case FundingType.NO_FUNDING_REQUIRED:
					isValid = true;
					break;
				case FundingType.COURIER: {
					const accountNumber =
						base?.fundingDetails?.courierDetails?.accountNumber;
					isValid = !!accountNumber;
					resultObject = {
						parentField: 'fundingDetails.courierDetails',
						label: 'Courier Details',
						fullFieldName: 'fundingDetails.courierDetails',
					};
					break;
				}
			}

			if (!isValid) {
				result.push(resultObject);
			}
		}

		return {
			missingFields: result,
			validationType: 'FUNDING_DETAILS',
			isValid: result.length === 0,
		};
	}

	static IsAddressValid(address: BaseTransactionAddress): boolean {
		return (
			address?.streetAddress1 &&
			address?.city &&
			address?.province &&
			address?.postalCode &&
			!!address?.country
		);
	}

	@Selector([
		RequestForFundsSelectors.RequestForFunds,
		MatterMortgagesState.requestForFundsValidationTemplate,
	])
	static ValidateFinalReport(
		base: RequestForFunds,
		validationTemplate: ValidationTemplate,
	): ValidationResult {
		if (!validationTemplate) {
			return;
		}

		const result = RequestForFundsSelectors.validateMortgageFromTemplate(
			base,
			validationTemplate,
		);

		return {
			missingFields: result,
			validationType: 'FINAL_REPORT',
			isValid: result.length === 0,
		};
	}

	//Selector to return all validations
	@Selector([
		RequestForFundsSelectors.ValidateRequestForFunds,
		RequestForFundsSelectors.ValidateFinalReport,
		RequestForFundsSelectors.ValidateFundingDetails,
	])
	static GetValidations(
		requestForFundsValidation: ValidationResult,
		finalReportValidation: ValidationResult,
		fundingDetailsValidation: ValidationResult,
	) {
		return {
			requestForFundsValidation,
			finalReportValidation,
			fundingDetailsValidation,
		};
	}

	@Selector([
		RequestForFundsSelectors.RequestBase,
		RequestForFundsSelectors.GetFundingDetails,
	])
	static RequestForFunds(
		base: MortgageRequestBase,
		fundingDetails: RequestForFundsFundingDetails,
	): RequestForFunds {
		return {
			...base,
			fundingDetails,
		};
	}
}
